import React from "react";
import PropTypes from "prop-types";
import ButtonBase from "@mui/material/ButtonBase";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";

import { colors } from "../../theme/colors";

const THUMB_SIZE = 18;
const TRACK_WIDTH = 44;
const TRACK_HEIGHT = 24;
const TRACK_PADDING_X = 2;
const TRACK_BORDER_WIDTH = 1;
const THUMB_TRAVEL =
    TRACK_WIDTH -
    TRACK_BORDER_WIDTH * 2 -
    TRACK_PADDING_X * 2 -
    THUMB_SIZE;

function ToggleButtonTrack({ active, sx = {}, ...other }) {
    return (
        <Box
            sx={{
                width: `${TRACK_WIDTH}px`,
                height: `${TRACK_HEIGHT}px`,
                boxSizing: "border-box",
                borderRadius: "999px",
                border: `1px solid ${
                    active
                        ? alpha(colors.primary.main, 0.22)
                        : alpha(colors.border.default, 0.08)
                }`,
                background: active
                    ? `
                        linear-gradient(
                            180deg,
                            ${alpha(colors.primary.main, 0.2)},
                            ${alpha(colors.primary.main, 0.08)}
                        )
                    `
                    : `
                        linear-gradient(
                            180deg,
                            rgba(42,42,46,.95),
                            rgba(28,28,32,.95)
                        )
                    `,
                position: "relative",
                flexShrink: 0,
                overflow: "hidden",
                boxShadow: active
                    ? `
                        0 0 14px rgba(255,180,70,.10),
                        inset 0 1px 0 rgba(255,255,255,.05)
                    `
                    : "inset 0 1px 0 rgba(255,255,255,.03)",
                transition: `
                    transform .28s cubic-bezier(.22,1,.36,1),
                    border-color .28s,
                    box-shadow .28s,
                    background .25s
                `,
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "1px",
                    background: `
                        linear-gradient(
                            90deg,
                            transparent,
                            rgba(255,255,255,.08),
                            transparent
                        )
                    `,
                    pointerEvents: "none",
                },
                "&:hover": {
                    borderColor: active
                        ? alpha(colors.primary.main, 0.35)
                        : alpha(colors.border.default, 0.15),
                    boxShadow: active
                        ? `
                            0 0 14px rgba(255,180,70,.10),
                            inset 0 1px 0 rgba(255,255,255,.05),
                            0 0 0 1px rgba(255,255,255,.02)
                        `
                        : `
                            inset 0 1px 0 rgba(255,255,255,.03),
                            0 0 0 1px rgba(255,255,255,.02)
                        `,
                },
                ...sx,
            }}
            {...other}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: `${TRACK_PADDING_X}px`,
                    width: `${THUMB_SIZE}px`,
                    height: `${THUMB_SIZE}px`,
                    transform: active
                        ? `translate(${THUMB_TRAVEL}px, -50%)`
                        : "translate(0, -50%)",
                    transition: `
                        transform .30s cubic-bezier(.22,1,.36,1),
                        background .25s,
                        box-shadow .25s
                    `,
                    willChange: "transform",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        background: colors.primary.main,
                        filter: "blur(5px)",
                        opacity: active ? 0.3 : 0,
                        transition: "opacity .30s",
                        pointerEvents: "none",
                    }}
                />
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        boxSizing: "border-box",
                        background: active
                            ? `
                                linear-gradient(
                                    180deg,
                                    ${colors.accent.main},
                                    ${colors.primary.main}
                                )
                            `
                            : `
                                linear-gradient(
                                    180deg,
                                    rgba(245,245,245,.96),
                                    rgba(205,205,205,.92)
                                )
                            `,
                        border: "1px solid rgba(255,255,255,.08)",
                        boxShadow: `
                            0 3px 10px rgba(0,0,0,.35),
                            inset 0 1px 0 rgba(255,255,255,.22)
                        `,
                        transition: "background .25s, box-shadow .25s",
                        pointerEvents: "none",
                    }}
                />
            </Box>
        </Box>
    );
}

export default function ToggleButton({
    active = false,
    onClick,
    disabled = false,
    sx = {},
}) {
    if (onClick) {
        return (
            <ButtonBase
                onClick={onClick}
                disabled={disabled}
                aria-pressed={active}
                disableRipple
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    m: 0,
                    lineHeight: 0,
                    verticalAlign: "middle",
                    borderRadius: "999px",
                    flexShrink: 0,
                }}
            >
                <ToggleButtonTrack active={active} sx={sx} />
            </ButtonBase>
        );
    }

    return (
        <ToggleButtonTrack
            active={active}
            aria-hidden
            sx={{
                display: "inline-flex",
                pointerEvents: "none",
                ...sx,
            }}
        />
    );
}

ToggleButton.propTypes = {
    active: PropTypes.bool,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    sx: PropTypes.object,
};
